import React, { useMemo, useState } from 'react';
import { LoanOfficerMetrics, TeamMetrics } from '../../types/throughput';
import { Card } from '../ui/Card';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp, Award, TrendingUp, AlertTriangle, ArrowLeft, Users } from 'lucide-react';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Tooltip } from 'recharts';
import { clsx } from 'clsx';
interface TeamLeaderboardProps {
  teamMetrics: TeamMetrics[];
  onTeamSelect: (teamLeadId: string | null) => void;
  selectedTeamId: string | null;
  onOfficerSelect?: (officerId: string | null) => void;
  selectedOfficerId?: string | null;
}
// Helper to normalize scores
const normalize = (val: number, min: number, max: number, reverse = false) => {
  if (max === min) return 100;
  const score = (val - min) / (max - min) * 100;
  return reverse ? 100 - score : score;
};
const getMinMax = (arr: number[]) => [Math.min(...arr), Math.max(...arr)];
export function TeamLeaderboard({
  teamMetrics,
  onTeamSelect,
  selectedTeamId,
  onOfficerSelect,
  selectedOfficerId
}: TeamLeaderboardProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // --- TEAM LEVEL CALCULATIONS ---
  const rankedTeams = useMemo(() => {
    if (!teamMetrics.length) return [];
    const pullThroughs = teamMetrics.map(m => m.conversion.createdToFunded);
    const cleanPcts = teamMetrics.map(m => m.cleanFiles.percentage);
    const dials = teamMetrics.map(m => m.avgDialsPerClaim);
    const costs = teamMetrics.map(m => m.costPerFunding);
    const [ptMin, ptMax] = getMinMax(pullThroughs);
    const [clMin, clMax] = getMinMax(cleanPcts);
    const [dlMin, dlMax] = getMinMax(dials);
    const [ctMin, ctMax] = getMinMax(costs);
    const scored = teamMetrics.map(m => {
      const ptScore = normalize(m.conversion.createdToFunded, ptMin, ptMax);
      const clScore = normalize(m.cleanFiles.percentage, clMin, clMax);
      const dlScore = normalize(m.avgDialsPerClaim, dlMin, dlMax, true);
      const ctScore = normalize(m.costPerFunding, ctMin, ctMax, true);
      const compositeScore = ptScore * 0.4 + clScore * 0.3 + dlScore * 0.15 + ctScore * 0.15;
      return {
        ...m,
        scores: {
          composite: compositeScore,
          pullThrough: ptScore,
          clean: clScore,
          dials: dlScore,
          cost: ctScore
        }
      };
    });
    return scored.sort((a, b) => b.scores.composite - a.scores.composite);
  }, [teamMetrics]);
  const companyAverages = useMemo(() => {
    if (!rankedTeams.length) return null;
    const avg = (key: keyof (typeof rankedTeams)[0]['scores']) => rankedTeams.reduce((sum, t) => sum + t.scores[key], 0) / rankedTeams.length;
    return {
      pullThrough: avg('pullThrough'),
      clean: avg('clean'),
      dials: avg('dials'),
      cost: avg('cost')
    };
  }, [rankedTeams]);
  // --- OFFICER LEVEL CALCULATIONS (if team selected) ---
  const selectedTeam = useMemo(() => {
    return rankedTeams.find(t => t.teamLead.id === selectedTeamId);
  }, [rankedTeams, selectedTeamId]);
  const rankedOfficers = useMemo(() => {
    if (!selectedTeam || !selectedTeam.memberMetrics.length) return [];
    const metrics = selectedTeam.memberMetrics;
    const pullThroughs = metrics.map(m => m.conversion.createdToFunded);
    const cleanPcts = metrics.map(m => m.cleanFiles.percentage);
    const dials = metrics.map(m => m.avgDialsPerClaim);
    const costs = metrics.map(m => m.costPerFunding);
    const [ptMin, ptMax] = getMinMax(pullThroughs);
    const [clMin, clMax] = getMinMax(cleanPcts);
    const [dlMin, dlMax] = getMinMax(dials);
    const [ctMin, ctMax] = getMinMax(costs);
    const scored = metrics.map(m => {
      const ptScore = normalize(m.conversion.createdToFunded, ptMin, ptMax);
      const clScore = normalize(m.cleanFiles.percentage, clMin, clMax);
      const dlScore = normalize(m.avgDialsPerClaim, dlMin, dlMax, true);
      const ctScore = normalize(m.costPerFunding, ctMin, ctMax, true);
      const compositeScore = ptScore * 0.4 + clScore * 0.3 + dlScore * 0.15 + ctScore * 0.15;
      return {
        ...m,
        scores: {
          composite: compositeScore,
          pullThrough: ptScore,
          clean: clScore,
          dials: dlScore,
          cost: ctScore
        }
      };
    });
    return scored.sort((a, b) => b.scores.composite - a.scores.composite);
  }, [selectedTeam]);
  const teamAverages = useMemo(() => {
    if (!rankedOfficers.length) return null;
    const avg = (key: keyof (typeof rankedOfficers)[0]['scores']) => rankedOfficers.reduce((sum, o) => sum + o.scores[key], 0) / rankedOfficers.length;
    return {
      pullThrough: avg('pullThrough'),
      clean: avg('clean'),
      dials: avg('dials'),
      cost: avg('cost')
    };
  }, [rankedOfficers]);
  // Company-wide officer averages (raw metrics across ALL teams, normalized to current team's scale)
  const companyOfficerScores = useMemo(() => {
    if (!selectedTeam || !teamMetrics.length) return null;
    // Collect all officer metrics across all teams
    const allOfficers = teamMetrics.flatMap(t => t.memberMetrics);
    if (!allOfficers.length) return null;
    // Compute raw averages
    const avgPT = allOfficers.reduce((s, m) => s + m.conversion.createdToFunded, 0) / allOfficers.length;
    const avgCl = allOfficers.reduce((s, m) => s + m.cleanFiles.percentage, 0) / allOfficers.length;
    const avgDl = allOfficers.reduce((s, m) => s + m.avgDialsPerClaim, 0) / allOfficers.length;
    const avgCt = allOfficers.reduce((s, m) => s + m.costPerFunding, 0) / allOfficers.length;
    // Normalize against the current team's officer min/max for consistent radar scale
    const metrics = selectedTeam.memberMetrics;
    if (!metrics.length) return null;
    const pullThroughs = metrics.map(m => m.conversion.createdToFunded);
    const cleanPcts = metrics.map(m => m.cleanFiles.percentage);
    const dials = metrics.map(m => m.avgDialsPerClaim);
    const costs = metrics.map(m => m.costPerFunding);
    const [ptMin, ptMax] = getMinMax(pullThroughs);
    const [clMin, clMax] = getMinMax(cleanPcts);
    const [dlMin, dlMax] = getMinMax(dials);
    const [ctMin, ctMax] = getMinMax(costs);
    return {
      pullThrough: normalize(avgPT, ptMin, ptMax),
      clean: normalize(avgCl, clMin, clMax),
      dials: normalize(avgDl, dlMin, dlMax, true),
      cost: normalize(avgCt, ctMin, ctMax, true)
    };
  }, [selectedTeam, teamMetrics]);
  // --- RENDER HELPERS ---
  const renderCoachingNotes = (scores: {
    pullThrough: number;
    clean: number;
    dials: number;
  }, averages: {
    pullThrough: number;
    clean: number;
    dials: number;
  }, rawMetrics: {
    pullThrough: number;
    clean: number;
    dials: number;
  }, isTeam: boolean) => {
    const strengths = [];
    const weaknesses = [];
    if (scores.pullThrough > averages.pullThrough + 10) strengths.push(`Excellent pull-through rate (${rawMetrics.pullThrough.toFixed(1)}%).`);
    if (scores.clean > averages.clean + 10) strengths.push(`High clean file submission rate (${rawMetrics.clean.toFixed(1)}%).`);
    if (scores.dials > averages.dials + 10) strengths.push(`Highly efficient lead claiming (${rawMetrics.dials.toFixed(1)} dials/claim).`);
    if (scores.pullThrough < averages.pullThrough - 10) weaknesses.push(`Pull-through is below ${isTeam ? 'company' : 'team'} average. Focus on pipeline management.`);
    if (scores.clean < averages.clean - 10) weaknesses.push(`Clean file rate is low (${rawMetrics.clean.toFixed(1)}%). Review submission guidelines.`);
    if (scores.dials < averages.dials - 10) weaknesses.push(`Dial efficiency is low (${rawMetrics.dials.toFixed(1)} dials/claim). Consider lead qualification training.`);
    if (strengths.length === 0) strengths.push('Consistent performer across the board.');
    if (weaknesses.length === 0) weaknesses.push('No major areas of concern. Keep up the good work.');
    return {
      strengths,
      weaknesses
    };
  };
  const renderRadarChart = (name: string, scores: any, averages: any, avgLabel: string, secondaryAverages?: any, secondaryLabel?: string) => {
    const radarData = [{
      subject: 'Pull-through',
      A: scores.pullThrough,
      B: averages.pullThrough,
      ...(secondaryAverages ? {
        C: secondaryAverages.pullThrough
      } : {}),
      fullMark: 100
    }, {
      subject: 'Clean Files',
      A: scores.clean,
      B: averages.clean,
      ...(secondaryAverages ? {
        C: secondaryAverages.clean
      } : {}),
      fullMark: 100
    }, {
      subject: 'Dial Efficiency',
      A: scores.dials,
      B: averages.dials,
      ...(secondaryAverages ? {
        C: secondaryAverages.dials
      } : {}),
      fullMark: 100
    }, {
      subject: 'Cost Efficiency',
      A: scores.cost,
      B: averages.cost,
      ...(secondaryAverages ? {
        C: secondaryAverages.cost
      } : {}),
      fullMark: 100
    }];
    return <div data-id="element-94">
        <h4 className="text-sm font-semibold text-[#181818] mb-4 text-center" data-id="element-95">
          Performance vs {avgLabel}
          {secondaryLabel ? ` & ${secondaryLabel}` : ''}
        </h4>
        <div className="h-[250px]" data-id="element-96">
          <ResponsiveContainer width="100%" height="100%" data-id="element-97">
            <RadarChart cx="50%" cy="50%" outerRadius="70%" data={radarData} data-id="element-98">
              <PolarGrid stroke="#E5E5E5" data-id="element-99" />
              <PolarAngleAxis dataKey="subject" tick={{
              fill: '#706E6B',
              fontSize: 12
            }} data-id="element-100" />
              <PolarRadiusAxis angle={30} domain={[0, 100]} tick={false} axisLine={false} data-id="element-101" />
              <Radar name={name} dataKey="A" stroke="#B8960C" fill="#B8960C" fillOpacity={0.5} data-id="element-102" />
              <Radar name={avgLabel} dataKey="B" stroke="#A0A0A0" fill="#A0A0A0" fillOpacity={0.2} data-id="element-103" />
              {secondaryAverages && <Radar name={secondaryLabel || 'Company Average'} dataKey="C" stroke="#4A90D9" fill="#4A90D9" fillOpacity={0.15} data-id="element-104" />}
              <Tooltip contentStyle={{
              borderRadius: '8px',
              border: 'none',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
            }} data-id="element-105" />
            </RadarChart>
          </ResponsiveContainer>
        </div>
        <div className="flex justify-center gap-4 mt-2" data-id="element-106">
          <div className="flex items-center gap-1.5" data-id="element-107">
            <div className="w-3 h-3 rounded-full bg-[#B8960C] opacity-50" data-id="element-108" />
            <span className="text-xs text-[#706E6B]" data-id="element-109">{name}</span>
          </div>
          <div className="flex items-center gap-1.5" data-id="element-110">
            <div className="w-3 h-3 rounded-full bg-[#A0A0A0] opacity-20" data-id="element-111" />
            <span className="text-xs text-[#706E6B]" data-id="element-112">{avgLabel}</span>
          </div>
          {secondaryAverages && secondaryLabel && <div className="flex items-center gap-1.5" data-id="element-113">
              <div className="w-3 h-3 rounded-full bg-[#4A90D9] opacity-40" data-id="element-114" />
              <span className="text-xs text-[#706E6B]" data-id="element-115">{secondaryLabel}</span>
            </div>}
        </div>
      </div>;
  };
  if (!rankedTeams.length || !companyAverages) return null;
  const isDrillDown = selectedTeamId !== null && selectedTeam;
  return <div className="flex flex-col gap-3 mb-10" data-id="element-116">
      <div className="flex items-center justify-between mb-2" data-id="element-117">
        <h2 className="text-xl font-semibold text-[#181818] flex items-center gap-2" data-id="element-118">
          {isDrillDown ? <>
              <button onClick={() => {
            if (selectedOfficerId) {
              onOfficerSelect?.(null);
            } else {
              onTeamSelect(null);
              setExpandedId(null);
            }
          }} className="p-1 hover:bg-[#E5E5E5] rounded-md transition-colors mr-1" data-id="element-119">
                <ArrowLeft className="w-5 h-5 text-[#706E6B]" data-id="element-120" />
              </button>
              <span className={clsx(selectedOfficerId && 'text-[#706E6B] cursor-pointer hover:text-[#181818] transition-colors')} onClick={selectedOfficerId ? () => onOfficerSelect?.(null) : undefined} data-id="element-121">
                {selectedTeam.teamLead.name}'s Team
              </span>
              {selectedOfficerId && (() => {
            const officer = selectedTeam.memberMetrics.find(m => m.officer.id === selectedOfficerId);
            return officer ? <>
                      <span className="text-[#706E6B]" data-id="element-122">/</span>
                      <span data-id="element-123">{officer.officer.name}</span>
                    </> : null;
          })()}
            </> : 'Team Leaderboard'}
        </h2>
      </div>

      {/* TEAM VIEW */}
      {!isDrillDown && rankedTeams.map((team, index) => {
      const isExpanded = expandedId === team.teamLead.id;
      const rank = index + 1;
      const isTop3 = rank <= 3;
      const {
        strengths,
        weaknesses
      } = renderCoachingNotes(team.scores, companyAverages, {
        pullThrough: team.conversion.createdToFunded,
        clean: team.cleanFiles.percentage,
        dials: team.avgDialsPerClaim
      }, true);
      return <Card key={team.teamLead.id} className={clsx('p-0 overflow-hidden transition-all duration-200', isExpanded ? 'ring-2 ring-[#B8960C] shadow-md' : 'hover:border-[#B8960C]/50')} noPadding data-id="element-124">
              <div className="flex flex-col sm:flex-row sm:items-center p-4 cursor-pointer bg-white gap-4 sm:gap-0" onClick={() => setExpandedId(isExpanded ? null : team.teamLead.id)} data-id="element-125">
                <div className="flex items-center" data-id="element-126">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#F5F5F5] mr-4 font-bold text-[#706E6B] shrink-0" data-id="element-127">
                    {isTop3 ? <Award className="w-5 h-5 text-[#B8960C]" data-id="element-128" /> : `#${rank}`}
                  </div>

                  <div className="w-48 pr-4" data-id="element-129">
                    <div className="font-semibold text-[#181818] truncate" data-id="element-130">
                      {team.teamLead.name}
                    </div>
                    <div className="text-xs text-[#706E6B] flex items-center gap-1 mt-0.5" data-id="element-131">
                      <Users className="w-3 h-3" data-id="element-132" />
                      {team.memberMetrics.length} Officers
                    </div>
                  </div>
                </div>

                <div className="flex-1 sm:mr-8" data-id="element-133">
                  <div className="flex items-center justify-between mb-1" data-id="element-134">
                    <span className="text-xs font-medium text-[#706E6B]" data-id="element-135">
                      Composite Score
                    </span>
                    <span className="text-xs font-bold text-[#181818]" data-id="element-136">
                      {team.scores.composite.toFixed(0)}
                    </span>
                  </div>
                  <div className="h-2 w-full bg-[#E5E5E5] rounded-full overflow-hidden" data-id="element-137">
                    <div className="h-full bg-[#B8960C] rounded-full transition-all duration-1000 ease-out" style={{
                width: `${team.scores.composite}%`
              }} data-id="element-138" />
                  </div>
                </div>

                <div className="hidden lg:flex items-center gap-6 mr-6" data-id="element-139">
                  <div className="flex flex-col items-end" data-id="element-140">
                    <span className="text-[10px] text-[#706E6B] uppercase tracking-wider" data-id="element-141">
                      Pull-through
                    </span>
                    <span className="text-sm font-medium" data-id="element-142">
                      {team.conversion.createdToFunded.toFixed(1)}%
                    </span>
                  </div>
                  <div className="flex flex-col items-end" data-id="element-143">
                    <span className="text-[10px] text-[#706E6B] uppercase tracking-wider" data-id="element-144">
                      Clean %
                    </span>
                    <span className="text-sm font-medium" data-id="element-145">
                      {team.cleanFiles.percentage.toFixed(1)}%
                    </span>
                  </div>
                  <div className="flex flex-col items-end" data-id="element-146">
                    <span className="text-[10px] text-[#706E6B] uppercase tracking-wider" data-id="element-147">
                      Dials
                    </span>
                    <span className="text-sm font-medium" data-id="element-148">
                      {team.avgDialsPerClaim.toFixed(1)}
                    </span>
                  </div>
                  <div className="flex flex-col items-end" data-id="element-149">
                    <span className="text-[10px] text-[#706E6B] uppercase tracking-wider" data-id="element-150">
                      Cost/Fund
                    </span>
                    <span className="text-sm font-medium" data-id="element-151">
                      ${team.costPerFunding.toFixed(0)}
                    </span>
                  </div>
                </div>

                <div className="text-[#706E6B] hidden sm:block" data-id="element-152">
                  {isExpanded ? <ChevronUp className="w-5 h-5" data-id="element-153" /> : <ChevronDown className="w-5 h-5" data-id="element-154" />}
                </div>
              </div>

              <AnimatePresence data-id="element-155">
                {isExpanded && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} transition={{
            duration: 0.3
          }} className="border-t border-[#E5E5E5] bg-[#FAFAFA]" data-id="element-156">
                    <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-157">
                      {renderRadarChart(team.teamLead.name, team.scores, companyAverages, 'Company Average')}

                      <div className="flex flex-col justify-center" data-id="element-158">
                        <div className="flex items-center justify-between mb-4" data-id="element-159">
                          <h4 className="text-sm font-semibold text-[#181818]" data-id="element-160">
                            Team Coaching Profile
                          </h4>
                          <button onClick={e => {
                    e.stopPropagation();
                    onTeamSelect(team.teamLead.id);
                  }} className="text-sm text-[#B8960C] hover:text-[#8A7109] font-medium flex items-center gap-1" data-id="element-161">
                            View Officers{' '}
                            <ChevronDown className="w-4 h-4 -rotate-90" data-id="element-162" />
                          </button>
                        </div>

                        <div className="space-y-4" data-id="element-163">
                          <div className="bg-[#04844B]/5 border border-[#04844B]/20 rounded-lg p-4" data-id="element-164">
                            <div className="flex items-center gap-2 mb-2" data-id="element-165">
                              <TrendingUp className="w-4 h-4 text-[#04844B]" data-id="element-166" />
                              <span className="font-semibold text-[#04844B]" data-id="element-167">
                                Strengths
                              </span>
                            </div>
                            <ul className="list-disc list-inside pl-1 space-y-1" data-id="element-168">
                              {strengths.map((s, i) => <li key={i} className="text-sm text-[#181818]" data-id="element-169">
                                  {s}
                                </li>)}
                            </ul>
                          </div>

                          <div className="bg-[#EA001E]/5 border border-[#EA001E]/20 rounded-lg p-4" data-id="element-170">
                            <div className="flex items-center gap-2 mb-2" data-id="element-171">
                              <AlertTriangle className="w-4 h-4 text-[#EA001E]" data-id="element-172" />
                              <span className="font-semibold text-[#EA001E]" data-id="element-173">
                                Areas for Growth
                              </span>
                            </div>
                            <ul className="list-disc list-inside pl-1 space-y-1" data-id="element-174">
                              {weaknesses.map((w, i) => <li key={i} className="text-sm text-[#181818]" data-id="element-175">
                                  {w}
                                </li>)}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </Card>;
    })}

      {/* OFFICER VIEW (Drill-down) */}
      {isDrillDown && teamAverages && rankedOfficers.map((officer, index) => {
      const isExpanded = expandedId === officer.officer.id;
      const rank = index + 1;
      const isTop3 = rank <= 3;
      const {
        strengths: teamStrengths,
        weaknesses: teamWeaknesses
      } = renderCoachingNotes(officer.scores, teamAverages, {
        pullThrough: officer.conversion.createdToFunded,
        clean: officer.cleanFiles.percentage,
        dials: officer.avgDialsPerClaim
      }, false);
      const {
        strengths: companyStrengths,
        weaknesses: companyWeaknesses
      } = companyOfficerScores ? renderCoachingNotes(officer.scores, companyOfficerScores, {
        pullThrough: officer.conversion.createdToFunded,
        clean: officer.cleanFiles.percentage,
        dials: officer.avgDialsPerClaim
      }, true) : {
        strengths: [],
        weaknesses: []
      };
      return <Card key={officer.officer.id} className={clsx('p-0 overflow-hidden transition-all duration-200', selectedOfficerId === officer.officer.id ? 'ring-2 ring-[#B8960C] shadow-md bg-[#B8960C]/5' : isExpanded ? 'ring-2 ring-[#B8960C] shadow-md' : 'hover:border-[#B8960C]/50')} noPadding data-id="element-176">
              <div className={clsx('flex flex-col sm:flex-row sm:items-center p-4 cursor-pointer gap-4 sm:gap-0', selectedOfficerId === officer.officer.id ? 'bg-[#B8960C]/5' : 'bg-white')} onClick={() => setExpandedId(isExpanded ? null : officer.officer.id)} data-id="element-177">
                <div className="flex items-center" data-id="element-178">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#F5F5F5] mr-4 font-bold text-[#706E6B] shrink-0" data-id="element-179">
                    {isTop3 ? <Award className="w-5 h-5 text-[#B8960C]" data-id="element-180" /> : `#${rank}`}
                  </div>

                  <button className={clsx('w-48 font-semibold truncate pr-4 text-left transition-colors', selectedOfficerId === officer.officer.id ? 'text-[#B8960C] underline' : 'text-[#181818] hover:text-[#B8960C] hover:underline')} onClick={e => {
              e.stopPropagation();
              if (selectedOfficerId === officer.officer.id) {
                onOfficerSelect?.(null);
              } else {
                onOfficerSelect?.(officer.officer.id);
              }
            }} title="Click to filter dashboard to this officer" data-id="element-181">
                    {officer.officer.name}
                  </button>
                </div>

                <div className="flex-1 sm:mr-8" data-id="element-182">
                  <div className="flex items-center justify-between mb-1" data-id="element-183">
                    <span className="text-xs font-medium text-[#706E6B]" data-id="element-184">
                      Composite Score
                    </span>
                    <span className="text-xs font-bold text-[#181818]" data-id="element-185">
                      {officer.scores.composite.toFixed(0)}
                    </span>
                  </div>
                  <div className="h-2 w-full bg-[#E5E5E5] rounded-full overflow-hidden" data-id="element-186">
                    <div className="h-full bg-[#B8960C] rounded-full transition-all duration-1000 ease-out" style={{
                width: `${officer.scores.composite}%`
              }} data-id="element-187" />
                  </div>
                </div>

                <div className="hidden lg:flex items-center gap-6 mr-6" data-id="element-188">
                  <div className="flex flex-col items-end" data-id="element-189">
                    <span className="text-[10px] text-[#706E6B] uppercase tracking-wider" data-id="element-190">
                      Pull-through
                    </span>
                    <span className="text-sm font-medium" data-id="element-191">
                      {officer.conversion.createdToFunded.toFixed(1)}%
                    </span>
                  </div>
                  <div className="flex flex-col items-end" data-id="element-192">
                    <span className="text-[10px] text-[#706E6B] uppercase tracking-wider" data-id="element-193">
                      Clean %
                    </span>
                    <span className="text-sm font-medium" data-id="element-194">
                      {officer.cleanFiles.percentage.toFixed(1)}%
                    </span>
                  </div>
                  <div className="flex flex-col items-end" data-id="element-195">
                    <span className="text-[10px] text-[#706E6B] uppercase tracking-wider" data-id="element-196">
                      Dials
                    </span>
                    <span className="text-sm font-medium" data-id="element-197">
                      {officer.avgDialsPerClaim.toFixed(1)}
                    </span>
                  </div>
                  <div className="flex flex-col items-end" data-id="element-198">
                    <span className="text-[10px] text-[#706E6B] uppercase tracking-wider" data-id="element-199">
                      Cost/Fund
                    </span>
                    <span className="text-sm font-medium" data-id="element-200">
                      ${officer.costPerFunding.toFixed(0)}
                    </span>
                  </div>
                </div>

                <div className="text-[#706E6B] hidden sm:block" data-id="element-201">
                  {isExpanded ? <ChevronUp className="w-5 h-5" data-id="element-202" /> : <ChevronDown className="w-5 h-5" data-id="element-203" />}
                </div>
              </div>

              <AnimatePresence data-id="element-204">
                {isExpanded && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} transition={{
            duration: 0.3
          }} className="border-t border-[#E5E5E5] bg-[#FAFAFA]" data-id="element-205">
                    <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-206">
                      {renderRadarChart(officer.officer.name, officer.scores, teamAverages, 'Team Average', companyOfficerScores, 'Company Average')}

                      <div className="flex flex-col justify-center gap-6" data-id="element-207">
                        {/* vs Team Average */}
                        <div data-id="element-208">
                          <h4 className="text-sm font-semibold text-[#181818] mb-3 flex items-center gap-2" data-id="element-209">
                            <span className="w-2.5 h-2.5 rounded-full bg-[#A0A0A0] inline-block" data-id="element-210" />
                            vs Team Average
                          </h4>
                          <div className="space-y-3" data-id="element-211">
                            <div className="bg-[#04844B]/5 border border-[#04844B]/20 rounded-lg p-3" data-id="element-212">
                              <div className="flex items-center gap-2 mb-1.5" data-id="element-213">
                                <TrendingUp className="w-3.5 h-3.5 text-[#04844B]" data-id="element-214" />
                                <span className="text-xs font-semibold text-[#04844B]" data-id="element-215">
                                  Strengths
                                </span>
                              </div>
                              <ul className="list-disc list-inside pl-1 space-y-0.5" data-id="element-216">
                                {teamStrengths.map((s, i) => <li key={i} className="text-xs text-[#181818]" data-id="element-217">
                                    {s}
                                  </li>)}
                              </ul>
                            </div>
                            <div className="bg-[#EA001E]/5 border border-[#EA001E]/20 rounded-lg p-3" data-id="element-218">
                              <div className="flex items-center gap-2 mb-1.5" data-id="element-219">
                                <AlertTriangle className="w-3.5 h-3.5 text-[#EA001E]" data-id="element-220" />
                                <span className="text-xs font-semibold text-[#EA001E]" data-id="element-221">
                                  Areas for Growth
                                </span>
                              </div>
                              <ul className="list-disc list-inside pl-1 space-y-0.5" data-id="element-222">
                                {teamWeaknesses.map((w, i) => <li key={i} className="text-xs text-[#181818]" data-id="element-223">
                                    {w}
                                  </li>)}
                              </ul>
                            </div>
                          </div>
                        </div>

                        {/* vs Company Average */}
                        {companyOfficerScores && <div data-id="element-224">
                            <h4 className="text-sm font-semibold text-[#181818] mb-3 flex items-center gap-2" data-id="element-225">
                              <span className="w-2.5 h-2.5 rounded-full bg-[#4A90D9] inline-block" data-id="element-226" />
                              vs Company Average
                            </h4>
                            <div className="space-y-3" data-id="element-227">
                              <div className="bg-[#04844B]/5 border border-[#04844B]/20 rounded-lg p-3" data-id="element-228">
                                <div className="flex items-center gap-2 mb-1.5" data-id="element-229">
                                  <TrendingUp className="w-3.5 h-3.5 text-[#04844B]" data-id="element-230" />
                                  <span className="text-xs font-semibold text-[#04844B]" data-id="element-231">
                                    Strengths
                                  </span>
                                </div>
                                <ul className="list-disc list-inside pl-1 space-y-0.5" data-id="element-232">
                                  {companyStrengths.map((s, i) => <li key={i} className="text-xs text-[#181818]" data-id="element-233">
                                      {s}
                                    </li>)}
                                </ul>
                              </div>
                              <div className="bg-[#EA001E]/5 border border-[#EA001E]/20 rounded-lg p-3" data-id="element-234">
                                <div className="flex items-center gap-2 mb-1.5" data-id="element-235">
                                  <AlertTriangle className="w-3.5 h-3.5 text-[#EA001E]" data-id="element-236" />
                                  <span className="text-xs font-semibold text-[#EA001E]" data-id="element-237">
                                    Areas for Growth
                                  </span>
                                </div>
                                <ul className="list-disc list-inside pl-1 space-y-0.5" data-id="element-238">
                                  {companyWeaknesses.map((w, i) => <li key={i} className="text-xs text-[#181818]" data-id="element-239">
                                      {w}
                                    </li>)}
                                </ul>
                              </div>
                            </div>
                          </div>}
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </Card>;
    })}
    </div>;
}